import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaList, FaChartPie, FaHome, FaTh, FaBox, FaTags, FaSignOutAlt, FaChevronRight, FaTimes, FaBars } from "react-icons/fa";
import { AuthContext } from "../components/AuthContext";
import { API_URL } from "../constant";

const Attributes = () => {
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    values: [""],
  });
  const [editingId, setEditingId] = useState(null);
  const [originalAttribute, setOriginalAttribute] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [showAddCard, setShowAddCard] = useState(false);
  const [nameAdded, setNameAdded] = useState(false);
  const [showValuesModal, setShowValuesModal] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState({ id: null, name: "", values: [] });
  const [valueError, setValueError] = useState(false);
  const [originalValues, setOriginalValues] = useState([]);

  // Fetch attributes from the backend
  const fetchAttributes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/attributes`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch attributes");
      }
      const data = await response.json();
      const attributesData = data.attributes || data;
      const sortedAttributes = attributesData.sort((a, b) => a.id - b.id);
      setAttributes(sortedAttributes);
    } catch (error) {
      console.error("Error fetching attributes:", error);
    }
  }, [authToken]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  // Fetch values for a specific attribute
  const fetchAttributeValues = async (attributeId) => {
    try {
      const response = await fetch(`${API_URL}/api/attributes/${attributeId}/values`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch attribute values");
      }
      const data = await response.json();
      return data.values || data;
    } catch (error) {
      console.error("Error fetching attribute values:", error);
      return [];
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddName = () => {
    if (!formData.name.trim()) {
      setModalMessage("Name is required.");
      setModalType("error");
      setShowModal(true);
      return;
    }
    setNameAdded(true);
    setModalMessage("Name added successfully!");
    setModalType("success");
    setShowModal(true);
    setTimeout(() => setShowModal(false), 2000);
  };

  const handleAddValueInput = () => {
    setFormData((prev) => ({ ...prev, values: [...prev.values, ""] }));
    setValueError(false);
  };

  const handleValueChange = (index, value) => {
    const updatedValues = [...formData.values];
    updatedValues[index] = value;
    setFormData((prev) => ({ ...prev, values: updatedValues }));
    setValueError(false);
  };

  const handleRemoveValue = (index) => {
    const updatedValues = formData.values.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, values: updatedValues }));
  };

  const handleViewValues = async (attribute) => {
    const values = await fetchAttributeValues(attribute.id);
    setSelectedAttribute({ ...attribute, values });
    setShowValuesModal(true);
  };

  // Add attribute function
  const addAttribute = async () => {
    const validValues = formData.values.filter((val) => val.trim() !== "");
    if (!formData.name.trim() || validValues.length === 0) {
      setModalMessage("All fields are required.");
      setModalType("error");
      setShowModal(true);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/attributes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ name: formData.name }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to add attribute");
      }
      const createdAttributeData = await response.json();
      const attributeId =
        createdAttributeData.id ||
        (createdAttributeData.attribute && createdAttributeData.attribute.id);
      if (!attributeId) {
        throw new Error("Attribute ID not returned");
      }
      for (let value of validValues) {
        const valueResponse = await fetch(
          `${API_URL}/api/attributes/${attributeId}/values`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ value }),
          }
        );
        if (!valueResponse.ok) {
          const text = await valueResponse.text();
          throw new Error(text || "Failed to add attribute value");
        }
        await valueResponse.json();
      }
      fetchAttributes();
      resetFormData();
      setModalMessage("Attribute added successfully!");
      setModalType("success");
      setShowModal(true);
    } catch (error) {
      console.error("Error adding attribute:", error);
      setModalMessage("Error adding attribute: " + error.message);
      setModalType("error");
      setShowModal(true);
    }
  };

  // Update attribute function.
  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      setModalMessage("Name is required.");
      setModalType("error");
      setShowModal(true);
      return;
    }
    try {
      const originalAttrName = originalAttribute ? originalAttribute.name : "";
      if (formData.name !== originalAttrName) {
        const response = await fetch(`${API_URL}/api/attributes/${formData.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ name: formData.name }),
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || "Failed to update attribute");
        }
        await response.json();
      }
      // Process new values
      const newValues = formData.values.filter(
        (val) => typeof val === "string" && val.trim() !== ""
      );
      for (let val of newValues) {
        await fetch(`${API_URL}/api/attributes/${formData.id}/values`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ value: val }),
        });
      }
      // Process removed values
      const removedValues = originalValues.filter(
        (ov) => !formData.values.includes(ov.value)
      );
      for (let ov of removedValues) {
        await fetch(`${API_URL}/api/attributes/${formData.id}/values/${ov.id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });
      }
      fetchAttributes();
      resetFormData();
      setOriginalValues([]);
      setModalMessage("Attribute updated successfully!");
      setModalType("success");
      setShowModal(true);
    } catch (error) {
      console.error("Error updating attribute:", error);
      setModalMessage("Error updating attribute: " + error.message);
      setModalType("error");
      setShowModal(true);
    }
  };

  // Modify handleEdit to receive the full attribute object.
  const handleEdit = async (attribute) => {
    setEditingId(attribute.id);
    setOriginalAttribute(attribute);
    const fetchedValues = await fetchAttributeValues(attribute.id);
    setOriginalValues(fetchedValues.filter((v) => typeof v === "object"));
    setFormData({
      id: attribute.id,
      name: attribute.name,
      values: fetchedValues.map((v) => (typeof v === "object" ? v.value : v)),
    });
    setNameAdded(true);
    setShowAddCard(true);
  };

  const handleSubmitAttribute = () => {
    if (editingId !== null) {
      handleUpdate();
    } else {
      addAttribute();
    }
  };

  // Modify handleDelete to receive the full attribute object.
  const handleDelete = (attribute) => {
    setAttributeToDelete(attribute);
    setShowDeleteModal(true);
  };

  const handleDeleteAttribute = async () => {
    try {
      const response = await fetch(`${API_URL}/api/attributes/${attributeToDelete.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Error deleting attribute");
      }
      fetchAttributes();
      setShowDeleteModal(false);
      setModalMessage("Attribute deleted successfully!");
      setModalType("success");
      setShowModal(true);
    } catch (error) {
      console.error("Error deleting attribute:", error);
      setModalMessage("Error deleting attribute: " + error.message);
      setModalType("error");
      setShowModal(true);
    }
  };

  const cancelDelete = () => setShowDeleteModal(false);

  const resetFormData = () => {
    setFormData({ id: null, name: "", values: [""] });
    setEditingId(null);
    setOriginalAttribute(null);
    setShowAddCard(false);
    setNameAdded(false);
  };

  const filteredAttributes = attributes.filter(
    ({ name, values }) =>
      name.toLowerCase().includes(search.toLowerCase()) ||
      (values &&
        values.some((value) =>
          typeof value === "object"
            ? value.value.toLowerCase().includes(search.toLowerCase())
            : value.toLowerCase().includes(search.toLowerCase())
        ))
  );

  const totalPages = Math.ceil(filteredAttributes.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAttributes.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 bg-white shadow-md">
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          <FaBars className="text-2xl text-gray-600" />
        </button>
        <span className="text-xl font-bold text-gray-700">Attributes</span>
      </div>

      <div className="flex flex-col md:flex-row min-h-screen overflow-hidden">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? "block" : "hidden"} md:block w-full md:w-64 bg-white shadow-md flex flex-col`}>
          <div className="hidden md:flex items-center justify-center py-6">
            <div className="text-3xl font-bold text-gray-600 flex items-center">
              <FaChartPie style={{ fontSize: "48px", marginRight: "10px" }} />
              <span className="text-2xl md:text-3xl font-bold" style={{ color: "gray-600" }}>
                Dashboard
              </span>
            </div>
          </div>
          {/* Top Section */}
          <nav className="mt-6 flex flex-col flex-grow text-gray-600">
            <ul>
              {[
                { name: "Home", icon: <FaHome className="mr-2" style={{ fontSize: "20px" }} />, link: "/Dashboard" },
                { name: "Categories", icon: <FaList className="mr-2" style={{ fontSize: "20px" }} />, link: "/Categories" },
                { name: "Subcategories", icon: <FaTh className="mr-2" style={{ fontSize: "20px" }} />, link: "/Subcategories" },
                { name: "Products", icon: <FaBox className="mr-2" style={{ fontSize: "20px" }} />, link: "/Products" },
                { name: "Attributes", icon: <FaTags className="mr-2" style={{ fontSize: "20px" }} />, link: "/Attributes" },
              ].map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => navigate(item.link)}
                  className={`cursor-pointer px-6 py-2 rounded-lg flex items-center justify-between text-lg ${
                    item.name === "Attributes" ? "bg-gray-200 scale-90" : "hover:bg-gray-200 hover:scale-90"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                  <FaChevronRight className="text-gray-600" />
                </li>
              ))}
            </ul>
          </nav>
          {/* Bottom Section */}
          <div className="mt-auto flex flex-col text-red-600">
            <ul>
              {[
                { name: "Signout", icon: (<FaSignOutAlt className="mr-2" style={{ fontSize: "20px" }} />) },
              ].map((item, idx) => (
                <li 
                  key={idx}
                  onClick={() => navigate("/Logout")}
                  className="cursor-pointer px-6 py-2 hover:bg-gray-200 rounded-lg flex items-center justify-between text-lg"
                >
                  <div className="flex items-center space-x-2">
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <FaTags className="text-gray-700 mr-2 text-2xl" />
              <span className="text-2xl font-bold text-gray-700">Attributes</span>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <button onClick={() => setShowAddCard(true)} className="bg-gray-500 text-white py-2 px-6 rounded hover:bg-gray-600 whitespace-nowrap">
                Add Attribute
              </button>
              <div className="w-full md:w-64">
                <input
                  placeholder="Search..."
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border border-gray-400 focus:ring-gray-500 rounded p-2 w-full"
                />
              </div>
            </div>
          </div>

          {/* Global Modal for messages */}
          {showModal && (
            <div className="fixed inset-0 z-[1000] bg-gray-400 bg-opacity-50 flex items-center justify-center" onClick={() => setShowModal(false)}>
              <div className="bg-white p-6 rounded shadow-md w-80" onClick={(e) => e.stopPropagation()}>
                <p className={`text-center ${modalType === "success" ? "text-black" : "text-red-500"}`}>
                  {modalMessage}
                </p>
                <div className="mt-4 text-center">
                  <button onClick={() => setShowModal(false)} className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">
                    {modalType === "success" ? "Done" : "Close"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
                <p>Are you sure you want to delete "{attributeToDelete?.name}"?</p>
                <div className="flex justify-end gap-4 mt-4">
                  <button onClick={() => handleDeleteAttribute()} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                    Delete
                  </button>
                  <button onClick={cancelDelete} className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add/Edit Attribute Modal */}
          {showAddCard && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center" onClick={() => setShowAddCard(false)}>
              <div className="bg-white p-6 rounded shadow-md w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-xl font-semibold mb-4">
                  {editingId !== null ? "Edit Attribute" : "Add Attribute"}
                </h2>
                <div className="flex items-center mb-4">
                  <input
                    placeholder="Name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="border border-gray-400 rounded p-2 w-full"
                  />
                  {!nameAdded && editingId === null && (
                    <button onClick={handleAddName} className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 ml-2">
                      Add
                    </button>
                  )}
                </div>
                {nameAdded && (
                  <div className="mb-4">
                    {formData.values.map((value, index) => (
                      <div key={index} className="flex items-center mb-2">
                        <input
                          placeholder="Value"
                          type="text"
                          value={value}
                          onChange={(e) => handleValueChange(index, e.target.value)}
                          className="border border-gray-400 rounded p-2 w-full"
                        />
                        <button onClick={() => handleRemoveValue(index)} className="ml-2 text-red-500 hover:text-red-700">
                          <FaTimes className="text-xl" />
                        </button>
                      </div>
                    ))}
                    {valueError && (<span className="text-red-500 text-sm mt-1">Value is required</span>)}
                    <div className="flex justify-end">
                      <button onClick={handleAddValueInput} className="bg-green-700 text-white py-2 px-10 rounded hover:bg-green-800">
                        Add Values
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-4">
                  <button onClick={() => setShowAddCard(false)} className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
                    Cancel
                  </button>
                  {nameAdded && (
                    <button onClick={editingId !== null ? handleUpdate : handleSubmitAttribute} className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">
                      {editingId !== null ? "Update" : "Add"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Attributes Table */}
          <div className="overflow-x-auto">
            <table className="table-auto w-full border-collapse border border-gray-400">
              <thead>
                <tr>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">ID</th>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">Attribute</th>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">Values</th>
                  <th className="px-4 md:px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((attribute) => (
                  <tr key={attribute.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 md:px-10 py-2 text-gray-700">{attribute.id}</td>
                    <td className="px-4 md:px-10 py-2 text-gray-700">{attribute.name}</td>
                    <td className="px-4 md:px-10 py-2 text-gray-700">
                      <button onClick={() => handleViewValues(attribute)} className="text-blue-500 py-1 px-0 rounded hover:text-blue-800">
                        View Values
                      </button>
                    </td>
                    <td className="px-4 md:px-10 py-2">
                      <button onClick={() => handleEdit(attribute)} className="bg-blue-500 text-white py-1 px-3 rounded hover:bg-blue-600 mr-2">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(attribute)} className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="mt-4 flex justify-center items-center gap-4">
            <button className={`px-4 py-2 rounded-md ${
                currentPage === 1 ? "bg-gray-300 cursor-not-allowed" : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              Previous
            </button>
            <span className="text-black">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button className={`px-4 py-2 rounded-md ${
                currentPage === totalPages || totalPages === 0 ? "bg-gray-300 cursor-not-allowed" : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Values Modal */}
      {showValuesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
            <button onClick={() => setShowValuesModal(false)} className="absolute top-3 right-4 text-red-500 hover:text-red-700">
              <FaTimes className="text-xl" />
            </button>
            <h3 className="text-lg font-bold mb-4">Values for "{selectedAttribute.name}"</h3>
            <ul>
              {selectedAttribute.values.map((item, index) => (
                <li key={index} className="text-gray-800">
                  {typeof item === "object" ? item.value : item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default Attributes;
